import { useEffect, useState, useCallback } from 'react'
import debounce from 'lodash.debounce'
import { parse, format } from 'date-fns'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCrefazContext } from '@/app/contexts/CrefazContext'
import { useOffersProposal } from '@/app/hooks/useOffersProposal'
import { crefazService } from '@/app/services/crefazService'
import { customerService } from '@/app/services/customerService'
import { transactionsService } from '@/app/services/transactionService'
import { PartnerType } from '@/app/utils/partners.enum'
import masks from '@/app/utils/masks'
import { useToast } from '@/view/components/ui/use-toast'

const getOffersModalSchema = z.object({
  phone: z.string().min(14, 'Telefone inválido').max(15, 'Telefone inválido'),
  email: z.string().min(1, 'E-mail obrigatório').email('E-mail inválido'),
  adicionais: z.any(),
})

type getOffersModalFormData = z.infer<typeof getOffersModalSchema>

interface UseOffersModalProps {
  onContinue?: () => void
}

export default function useOffersModal({ onContinue }: UseOffersModalProps) {
  const getOffersModalForm = useForm<getOffersModalFormData>({
    resolver: zodResolver(getOffersModalSchema),
    mode: 'onChange',
  })

  const { handleSubmit } = getOffersModalForm
  const { toast } = useToast()

  const { proposalData, customerData, setCustomerData, setProposalData } =
    useCrefazContext()

  const [selectedOfferIndex, setSelectedOfferIndex] = useState<number>(0)
  const [selectedValues, setSelectedValues] = useState<Record<number, number>>(
    {},
  )
  const [installmentsByOfferIndex, setInstallmentsByOfferIndex] = useState<
    Record<number, { prazo: number; valor: number }[]>
  >({})
  const [loadingInstallments, setLoadingInstallments] = useState<
    Record<number, boolean>
  >({})
  const [selectedInstallments, setSelectedInstallments] = useState<
    Record<number, { prazo: number; valor: number }>
  >({})
  const [commissionByOfferIndex, setCommissionByOfferIndex] = useState<
    Record<number, number | undefined>
  >({})

  const [inputDigits, setInputDigits] = useState<string>(
    ((selectedValues[selectedOfferIndex] ?? 0) * 100).toString(),
  )

  useEffect(() => {
    const value = selectedValues[selectedOfferIndex] ?? 0
    setInputDigits(Math.round(value * 100).toString())
  }, [selectedValues, selectedOfferIndex])

  const {
    data: offersData,
    isLoading: isLoadingOffersData,
    errorMessage,
  } = useOffersProposal(proposalData?.propostaId)

  async function handleSelectOffer(index: number, valor: number) {
    const offer = offersData?.offers?.[index]
    if (!offer) return

    setCommissionByOfferIndex((prev) => ({
      ...prev,
      [index]: undefined,
    }))

    setLoadingInstallments((prev) => ({ ...prev, [index]: true }))

    try {
      const response = await crefazService.selectOffer(
        proposalData?.propostaId!,
        {
          produtoId: offer.produtoId,
          convenioId: offer.convenioId,
          tabelaJurosId: offer.tabelaJurosId,
          renda: offersData.proposta.valorRendaPresumida,
          valor,
          tipoCalculo: 0,
        },
      )

      if (response?.data?.prazoValor) {
        const installments = response.data.prazoValor
        setInstallmentsByOfferIndex((prev) => ({
          ...prev,
          [index]: installments,
        }))

        if (installments.length > 0) {
          const firstInstallment = installments[0]
          setSelectedInstallments((prev) => ({
            ...prev,
            [index]: firstInstallment,
          }))
          handleSelectPrazo(index, firstInstallment.prazo)
        }
      }
    } catch (err) {
      console.error('Erro ao selecionar oferta:', err)
    } finally {
      setLoadingInstallments((prev) => ({ ...prev, [index]: false }))
    }
  }

  async function handleSelectPrazo(index: number, prazo: number) {
    try {
      const response = await crefazService.getCommission({ prazo })
      if (response?.commission !== undefined) {
        setCommissionByOfferIndex((prev) => ({
          ...prev,
          [index]: response.commission,
        }))
      }
    } catch (err) {
      console.error('Erro ao buscar comissão:', err)
    }
  }

  const isProposalReady = (index: number) => {
    return !!selectedValues[index] && !!selectedInstallments[index]
  }

  useEffect(() => {
    if (offersData?.offers) {
      const initialValues: Record<number, number> = {}

      offersData.offers.forEach((offer, index) => {
        const valoresOrdenados = [...offer.valores].sort(
          (a, b) => b.valor - a.valor,
        )
        const valor = valoresOrdenados[0]?.valor ?? 0
        initialValues[index] = valor

        handleSelectOffer(index, valor)
      })

      setSelectedValues(initialValues)
    }
  }, [offersData])

  const debouncedFetchInstallments = useCallback(
    debounce((index: number, valor: number) => {
      handleSelectOffer(index, valor)
    }, 500),
    [offersData, proposalData],
  )

  const findClosestAllowedValue = (value: number, allowedValues: number[]) => {
    if (allowedValues.length === 0) return value

    return allowedValues.reduce((prev, curr) => {
      return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    })
  }

  const handleSliderChange = (value: number) => {
    const offer = offersData?.offers?.[selectedOfferIndex]
    if (!offer) return

    const allowedValues = offer.valores.map((v) => v.valor)
    const closestValue = findClosestAllowedValue(value, allowedValues)

    setSelectedValues((prev) => ({
      ...prev,
      [selectedOfferIndex]: closestValue,
    }))

    debouncedFetchInstallments(selectedOfferIndex, closestValue)
  }

  useEffect(() => {
    if (customerData) {
      if (customerData.phone) {
        getOffersModalForm.setValue('phone', masks.phone(customerData.phone))
      }
    }
  }, [customerData, getOffersModalForm])

  const [isSendingData, setIsSendingData] = useState(false)

  const { setTransactionId } = useCrefazContext()

  const validateAdicionais = (): boolean => {
    const offer = offersData?.offers?.[selectedOfferIndex]
    if (!offer?.adicionais?.length) return true

    let isValid = true
    offer.adicionais.forEach((adic) => {
      const fieldName = `adicionais.${selectedOfferIndex}.${adic.convenioDadosId}`
      const value = getOffersModalForm.getValues(fieldName as any)
      if (!value || String(value).trim() === '') {
        getOffersModalForm.setError(fieldName as any, {
          type: 'manual',
          message: 'Campo obrigatório',
        })
        isValid = false
      } else {
        getOffersModalForm.clearErrors(fieldName as any)
      }
    })

    return isValid
  }

  const submitModal = handleSubmit(async (formData: any) => {
    try {
      if (!validateAdicionais()) return
      if (!customerData || !proposalData) return

      setIsSendingData(true)

      const customerPayload = {
        name: customerData.name,
        cpf: customerData.cpf,
        phone:
          formData.phone.replace(/\D/g, '') ||
          customerData?.phone?.replace(/\D/g, ''),
        email: formData.email || customerData.email,
        birthDate: new Date(customerData.birthDate).toISOString(),
        address: customerData.address
          ? {
              zipCode: customerData.address.zipCode,
              state: customerData.address.state,
              city: customerData.address.city,
              neighborhood: customerData.address.neighborhood || '',
              street: customerData.address.street || '',
              number: customerData.address.number || '',
              complement: customerData.address.complement || '',
            }
          : undefined,
      }

      const customerResponse =
        await customerService.createCustomer(customerPayload)

      setCustomerData({
        ...customerData,
        phone: customerPayload.phone,
        email: customerPayload.email,
        address: customerPayload.address
          ? {
              zipCode: customerPayload.address.zipCode,
              state: customerPayload.address.state,
              city: customerPayload.address.city,
              neighborhood: customerPayload.address.neighborhood,
              street: customerPayload.address.street,
              number: customerPayload.address.number,
              complement: customerPayload.address.complement,
            }
          : customerData.address,
      })

      const offer = offersData?.offers?.[selectedOfferIndex]
      const selectedInstallment = selectedInstallments[selectedOfferIndex]
      const selectedValue = selectedValues[selectedOfferIndex]

      if (!offer || !selectedInstallment || !selectedValue) {
        setIsSendingData(false)
        return
      }

      const adicionaisPayload = offer.adicionais.map((adic) => {
        let valor =
          formData.adicionais?.[selectedOfferIndex]?.[adic.convenioDadosId]

        if (adic.nome === 'Data de Leitura' && valor) {
          const parsedDate = parse(valor, 'dd/MM/yyyy', new Date())
          valor = format(parsedDate, 'yyyy-MM-dd')
        }

        return {
          name: adic.nome,
          convenioDadosId: adic.convenioDadosId,
          valor: String(valor),
          convenioId: offer.convenioId,
        }
      })

      const withdrawPayload = {
        type: PartnerType.CREFAZ,
        value: selectedValue * 100,
        customerId: customerResponse._id,
        Prazo: selectedInstallment.prazo,
        propostaId: proposalData.propostaId,
        prestacao: selectedInstallment.valor,
        adicionais: adicionaisPayload,
      }

      const withdrawResponse =
        await transactionsService.requestWithdraw(withdrawPayload)

      setTransactionId(withdrawResponse._id)
      setProposalData({
        value: selectedValue * 100,
        propostaId: proposalData.propostaId,
      })
      onContinue?.()
    } catch (error: any) {
      console.error('Erro ao processar proposta:', error)
      toast({
        title: 'Erro ao processar proposta',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Erro inesperado. Tente novamente mais tarde.',
        variant: 'destructive',
      })
    } finally {
      setIsSendingData(false)
    }
  })

  return {
    form: getOffersModalForm,
    offersData,
    isLoadingOffersData,
    selectedOfferIndex,
    setSelectedOfferIndex,
    selectedValues,
    setSelectedValues,
    installmentsByOfferIndex,
    handleSelectOffer,
    loadingInstallments,
    setSelectedInstallments,
    isProposalReady,
    commissionByOfferIndex,
    setCommissionByOfferIndex,
    handleSelectPrazo,
    errorMessage,
    submitModal,
    handleSliderChange,
    selectedInstallments,
    customerData,
    isSendingData,
    inputDigits,
    setInputDigits,
  }
}
